import clsx from "clsx";
import Image from "next/image";
import DomPurify from "isomorphic-dompurify";
import CustomImageComp from "components/Image";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import { FadeIn } from "components/ui/FadeIn";

import LeadForm from "components/Tiles/commonComponents/LeadForm";
import { APP_LANGUAGES } from "utils/language";
import "./style.css";
interface ContactFormProps {
  bgColor?: string;
  isTop?: boolean;
  fullOverlay?: boolean;
  isTall?: boolean;
  src?: string;
  alt?: string;
  miniTitleColor?: string;
  title: string;
  subtitle: string;
  titleTextColor?: string; // New optional prop for title text color
  subtitleTextColor?: string;
  miniTitle?: string;
  buttonText: string;
  quizSlug?: string;
  visibleFields: {
    name: boolean;
    email: boolean;
    phone: boolean;
    address: boolean;
  };
  showOverlay?: boolean;
  redirectToQuiz?: boolean;
  webhookUrl?: string;
  images?: {
    src: string;
    alt?: string;
  }[];
  language?: APP_LANGUAGES;
  redirectSlug?: string;
}

const ContactForm = ({
  bgColor,
  fullOverlay = true,
  isTop = false,
  isTall = false,
  alt = "contact-form-background-image",
  src,
  miniTitle,
  miniTitleColor,
  title,
  subtitle,
  titleTextColor = "#073937",
  subtitleTextColor = "#073937",
  visibleFields,
  buttonText = "Get a Cash Offer",
  quizSlug = "fast-cash-offer",
  showOverlay = true,
  redirectToQuiz = false,
  webhookUrl,
  images,
  language = APP_LANGUAGES.ENGLISH,
  redirectSlug
}: ContactFormProps) => {
  const onlyAddressFieldVisible =
    Object.values(visibleFields).filter((val) => val).length === 1 && visibleFields.address;

  return (
    <section
      id="ContactForm-1"
      style={{ backgroundColor: bgColor }}
      className={clsx("relative @container", isTop && "-mt-[82px]")} // Conditional classes based on isTop
    >
      {!!((showOverlay || isTop) && src) && <div className={clsx("bnbb", fullOverlay && "lgs")} />}
      <div className={clsx("relative flex w-full", isTall ? "min-h-[81vh]" : src ? "min-h-[600px]" : "")}>
        {src && (
          <Image
            src={src}
            alt={alt}
            quality={90}
            fill
            priority
            style={{
              objectFit: "cover",
              objectPosition: "50% 62%"
            }}
          />
        )}
        <div className="relative z-20 flex flex-1 items-center justify-center">
          <FadeIn className="mx-auto w-full text-center max-sm:mt-20">
            {miniTitle && (
              <p className="mb-4 text-center text-[18px] font-semibold text-white" style={{ color: miniTitleColor }}>
                {miniTitle}
              </p>
            )}
            {title && (
              <h1
                className="mb-6 text-balance text-center font-heading text-[42px] leading-[48px] lg:text-[52px] lg:leading-[64px]"
                style={{ color: titleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }} // Render the HTML string
              />
            )}
            {subtitle && (
              <h2
                className="relative mx-auto mb-12 mt-6 max-w-3xl px-12 text-subtitle leading-8"
                style={{ color: subtitleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle || "") }} // Render the HTML string
              />
            )}

            <MaxWidthWrapper
              className={clsx(
                "flex max-w-screen-md flex-col items-center justify-center gap-2",
                onlyAddressFieldVisible && "md:flex-row lg:gap-3"
              )}
            >
              <LeadForm
                visibleFields={visibleFields}
                buttonText={buttonText}
                redirectToQuiz={redirectToQuiz}
                quizSlug={quizSlug}
                webhookUrl={webhookUrl}
                language={language}
                redirectSlug={redirectSlug}
              />
            </MaxWidthWrapper>
            {!!images?.length && (
              <div className="mx-auto mt-2 flex max-w-screen-md flex-wrap gap-2 px-4 lg:px-12 xl:px-16 2xl:px-20">
                {images.map((image, index) => (
                  <div key={index} className={clsx("relative h-20 flex-1 rounded-2xl")}>
                    <CustomImageComp
                      src={image.src}
                      fill
                      alt={image.alt || `contact_form_image_${index}`}
                      className="absolute inset-0 h-full w-full"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
